import {
  BottomToTop,
  BuildingStorefront,
  Buildings,
  Channels,
  ChatBubble,
  ChevronDownMini,
  CogSixTooth,
  CubeSolid,
  CurrencyDollar,
  DocumentText,
  EllipsisHorizontal,
  House,
  Levels,
  MinusMini,
  OpenRectArrowOut,
  Plus,
  ReceiptPercent,
  Shopping,
  ShoppingCart,
  SquaresPlus,
  Tag,
  TruckFast,
  User,
  Users,
} from "@medusajs/icons";
import { Avatar, Divider, DropdownMenu, Text, clx } from "@medusajs/ui";

import { Collapsible as RadixCollapsible } from "radix-ui";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useLogout } from "../../../hooks/api";
import { useStore } from "../../../hooks/api/store";
import { useDocumentDirection } from "../../../hooks/use-document-direction";
import { queryClient } from "../../../lib/query-client";
import { useExtension } from "../../../providers/extension-provider";
import { Skeleton } from "../../common/skeleton";
import { INavItem, NavItem } from "../../layout/nav-item";
import { Shell } from "../../layout/shell";
import { UserMenu } from "../user-menu";

export const WWMainLayout = () => {
  return (
    <Shell>
      <MainSidebar />
    </Shell>
  );
};

const MainSidebar = () => {
  return (
    <aside className="flex flex-1 flex-col justify-between overflow-y-auto">
      <div className="flex flex-1 flex-col">
        <div className="sticky top-0 bg-ui-bg-subtle">
          <Header />
          <div className="px-3">
            <Divider variant="dashed" />
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="flex flex-1 flex-col">
            <CoreRouteSection />
            <ExtensionRouteSection />
          </div>
          <UtilitySection />
        </div>
        <div className="sticky bottom-0 bg-ui-bg-subtle">
          <UserSection />
        </div>
      </div>
    </aside>
  );
};

const Logout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { mutateAsync: logoutMutation } = useLogout();

  const handleLogout = async () => {
    await logoutMutation(undefined, {
      onSuccess: () => {
        /**
         * When the user logs out, we want to clear the query cache
         */
        queryClient.clear();
        navigate("/login");
      },
    });
  };

  return (
    <DropdownMenu.Item onClick={handleLogout}>
      <div className="flex items-center gap-x-2">
        <OpenRectArrowOut className="text-ui-fg-subtle" />
        <span>{t("app.menus.actions.logout")}</span>
      </div>
    </DropdownMenu.Item>
  );
};

const Header = () => {
  const { t } = useTranslation();
  const { store, isPending, isError, error } = useStore();
  const direction = useDocumentDirection();
  const name = store?.name;
  const fallback = store?.name?.slice(0, 1).toUpperCase();

  const isLoaded = !isPending && !!store && !!name && !!fallback;

  if (isError) {
    throw error;
  }

  return (
    <div className="w-full p-3">
      <DropdownMenu dir={direction}>
        <DropdownMenu.Trigger
          disabled={!isLoaded}
          className={clx(
            "grid w-full grid-cols-[24px_1fr_15px] items-center gap-x-3 rounded-md bg-ui-bg-subtle p-0.5 pe-2 outline-none transition-fg",
            "hover:bg-ui-bg-subtle-hover",
            "data-[state=open]:bg-ui-bg-subtle-hover",
            "focus-visible:shadow-borders-focus",
          )}
        >
          {fallback ? (
            <Avatar variant="squared" size="xsmall" fallback={fallback} />
          ) : (
            <Skeleton className="h-6 w-6 rounded-md" />
          )}
          <div className="block overflow-hidden text-start">
            {name ? (
              <Text
                size="small"
                weight="plus"
                leading="compact"
                className="truncate"
              >
                {store.name}
              </Text>
            ) : (
              <Skeleton className="h-[9px] w-[120px]" />
            )}
          </div>
          <EllipsisHorizontal className="text-ui-fg-muted" />
        </DropdownMenu.Trigger>
        {isLoaded && (
          <DropdownMenu.Content className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-0">
            <div className="flex items-center gap-x-3 px-2 py-1">
              <Avatar variant="squared" size="small" fallback={fallback} />
              <div className="flex flex-col overflow-hidden">
                <Text
                  size="small"
                  weight="plus"
                  leading="compact"
                  className="truncate"
                >
                  {name}
                </Text>
                <Text
                  size="xsmall"
                  leading="compact"
                  className="text-ui-fg-subtle"
                >
                  {t("app.nav.main.store")}
                </Text>
              </div>
            </div>
            <DropdownMenu.Separator />
            <DropdownMenu.Item className="gap-x-2" asChild>
              <Link to="/settings/store">
                <BuildingStorefront className="text-ui-fg-subtle" />
                {t("app.nav.main.storeSettings")}
              </Link>
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
            <Logout />
          </DropdownMenu.Content>
        )}
      </DropdownMenu>
    </div>
  );
};

const useCoreRoutes = (): Omit<INavItem, "pathname">[] => {
  const { t } = useTranslation();

  return [
    {
      icon: <House />,
      label: "Dashboard",
      to: "/dashboard",
      items: [
        // TODO: Enable when domin is introduced
        // {
        //   label: t("draftOrders.domain"),
        //   to: "/draft-orders",
        // },
      ],
    },
    {
      icon: <DocumentText />,
      label: "Pending Orders",
      to: "/pending-orders",
      items: [],
    },
    {
      icon: <User />,
      label: "Accounts",
      to: "/accounts",
      items: [],
    },
    {
      icon: <Plus />,
      label: "Patients",
      to: "/patients",
      items: [],
    },
    // {
    //   icon: <CubeSolid />,
    //   label: "Products",
    //   to: "/products",
    //   items: [],
    // },
    // {
    //   icon: <Levels />,
    //   label: "Sales",
    //   to: "/sales",
    //   items: [],
    // },
    {
      icon: <ShoppingCart />,
      label: t("orders.domain"),
      to: "/orders",
      items: [
        // TODO: Enable when domin is introduced
        // {
        //   label: t("draftOrders.domain"),
        //   to: "/draft-orders",
        // },
      ],
    },
    {
      icon: <Tag />,
      label: t("products.domain"),
      to: "/products",
      items: [
        {
          label: t("collections.domain"),
          to: "/collections",
        },
        {
          label: t("categories.domain"),
          to: "/categories",
        },
        // TODO: Enable when domin is introduced
        // {
        //   label: t("giftCards.domain"),
        //   to: "/gift-cards",
        // },
      ],
    },
    {
      icon: <Buildings />,
      label: t("inventory.domain"),
      to: "/inventory",
      items: [
        {
          label: t("reservations.domain"),
          to: "/reservations",
        },
      ],
    },
    {
      icon: <Users />,
      label: t("customers.domain"),
      to: "/customers",
      items: [
        {
          label: t("customerGroups.domain"),
          to: "/customer-groups",
        },
      ],
    },
    {
      icon: <Shopping />,
      label: t("sellers.domain"),
      to: "/sellers",
    },
    {
      icon: <ReceiptPercent />,
      label: t("promotions.domain"),
      to: "/promotions",
      items: [
        {
          label: t("campaigns.domain"),
          to: "/campaigns",
        },
      ],
    },
    {
      icon: <CurrencyDollar />,
      label: t("priceLists.domain"),
      to: "/price-lists",
    },
    {
      icon: <BottomToTop />,
      label: t("requests.domain"),
      to: "requests/seller",
      items: [
        {
          label: t("requests.seller"),
          to: "/requests/seller",
        },
        {
          label: t("requests.product"),
          to: "/requests/product/",
        },
        {
          label: t("requests.product-tag"),
          to: "/requests/product-tag",
        },
        {
          label: t("requests.product-type"),
          to: "/requests/product-type",
        },
        {
          label: t("requests.review-remove"),
          to: "/requests/review-remove",
        },
        {
          label: t("requests.product-update"),
          to: "/requests/product-update",
        },
        {
          label: t("requests.return"),
          to: "/requests/return",
        },
        {
          label: t("requests.product-category"),
          to: "/requests/product-category",
        },
        {
          label: t("requests.product-collection"),
          to: "/requests/product-collection",
        },
      ],
    },
    // {
    //   icon: <ChatBubble />,
    //   label: t("messages.domain"),
    //   to: "/messages",
    // },
    {
      icon: <Channels />,
      label: "Plato",
      to: "/plato-patients",
      items: [
        {
          label: "Patients",
          to: "/plato-patients",
        },
        {
          label: "Inventory",
          to: "/plato-inventory",
        },
        {
          label: "Invoice",
          to: "/plato-invoice",
        },
      ],
    },
    {
      icon: <TruckFast />,
      label: "LalaMove",
      to: "/lalamove-balance",
      items: [
        {
          label: "LalaMove Balance",
          to: "/lalamove-balance",
        },
      ],
    },
  ];
};

// const Searchbar = () => {
//   const { t } = useTranslation()
//   const { toggleSearch } = useSearch()

//   return (
//     <div className="px-3">
//       <button
//         onClick={toggleSearch}
//         className={clx(
//           "bg-ui-bg-subtle text-ui-fg-subtle flex w-full items-center gap-x-2.5 rounded-md px-2 py-1 outline-none",
//           "hover:bg-ui-bg-subtle-hover",
//           "focus-visible:shadow-borders-focus"
//         )}
//       >
//         <MagnifyingGlass />
//         <div className="flex-1 text-left">
//           <Text size="small" leading="compact" weight="plus">
//             {t("app.search.label")}
//           </Text>
//         </div>
//         <Text size="small" leading="compact" className="text-ui-fg-muted">
//           ⌘K
//         </Text>
//       </button>
//     </div>
//   )
// }

const CollapsibleSection = ({
  title,
  routes,
}: {
  title: string;
  routes: any[];
}) => {
  return (
    <RadixCollapsible.Root defaultOpen>
      <div className="px-4">
        <RadixCollapsible.Trigger asChild className="group/trigger">
          <button className="flex w-full items-center justify-between px-2 py-2 text-ui-fg-subtle">
            <Text size="xsmall" weight="plus" leading="compact">
              {title}
            </Text>
            <div className="text-ui-fg-muted">
              <ChevronDownMini className="group-data-[state=open]/trigger:hidden" />
              <MinusMini className="group-data-[state=closed]/trigger:hidden" />
            </div>
          </button>
        </RadixCollapsible.Trigger>
      </div>

      <RadixCollapsible.Content>
        <nav className="flex flex-col gap-y-0.5 py-1 pb-4">
          {routes.map((route) => (
            <div key={route.to} className="px-3 py-1">
              <NavItem {...route} />
            </div>
          ))}
        </nav>
      </RadixCollapsible.Content>
    </RadixCollapsible.Root>
  );
};

const CoreRouteSection = () => {
  const coreRoutes = useCoreRoutes();
  const { getMenu } = useExtension();
  const menuItems = getMenu("coreExtensions");

  // merge nested extension items
  menuItems.forEach((item) => {
    if (item.nested) {
      const route = coreRoutes.find((route) => route.to === item.nested);
      if (route) {
        route.items?.push(item);
      }
    }
  });

  return (
    <nav className="flex flex-col gap-y-1 py-3">
      <CollapsibleSection title="General" routes={coreRoutes.slice(0, 4)} />
      <CollapsibleSection title="E-commerce" routes={coreRoutes.slice(4, 12)} />
      <CollapsibleSection title="Integration" routes={coreRoutes.slice(12)} />
    </nav>
  );
};

const ExtensionRouteSection = () => {
  const { t } = useTranslation();
  const { getMenu } = useExtension();

  const menuItems = getMenu("coreExtensions").filter((item) => !item.nested);

  if (!menuItems.length) {
    return null;
  }

  return (
    <div>
      <div className="px-3">
        <Divider variant="dashed" />
      </div>
      <div className="flex flex-col gap-y-1 py-3">
        {/* <RadixCollapsible.Root defaultOpen> */}
        <RadixCollapsible.Root>
          <div className="px-4">
            <RadixCollapsible.Trigger asChild className="group/trigger">
              <button className="flex w-full items-center justify-between px-2 text-ui-fg-subtle">
                <Text size="xsmall" weight="plus" leading="compact">
                  {t("app.nav.common.extensions")}
                </Text>
                <div className="text-ui-fg-muted">
                  <ChevronDownMini className="group-data-[state=open]/trigger:hidden" />
                  <MinusMini className="group-data-[state=closed]/trigger:hidden" />
                </div>
              </button>
            </RadixCollapsible.Trigger>
          </div>
          <RadixCollapsible.Content>
            <nav className="flex flex-col gap-y-0.5 py-1 pb-4">
              {menuItems.map((item, i) => {
                return (
                  <NavItem
                    key={i}
                    to={item.to}
                    label={item.label}
                    icon={item.icon ? item.icon : <SquaresPlus />}
                    items={item.items}
                    type="extension"
                  />
                );
              })}
            </nav>
          </RadixCollapsible.Content>
        </RadixCollapsible.Root>
      </div>
    </div>
  );
};

const UtilitySection = () => {
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-y-0.5 py-3">
      <NavItem
        label={t("app.nav.settings.header")}
        to="/settings"
        from={location.pathname}
        icon={<CogSixTooth />}
      />
    </div>
  );
};

const UserSection = () => {
  return (
    <div>
      <div className="px-3">
        <Divider variant="dashed" />
      </div>
      <UserMenu />
    </div>
  );
};
